import { isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, PLATFORM_ID, ViewChild } from '@angular/core';

interface MultiplierStep {
  value: string;
  transitionMs: number;
  holdMs: number;
}

/**
 * Indicative-multiplier animation timeline: every value rolls up into place on a two-line
 * odometer reel (old value slides out the top while the new value slides in from below), holds,
 * then rolls to the next value. Once the final 100X has settled, a cyan glow + zoom pulse lands
 * on it, then it holds for 4-5s before looping.
 *
 *   roll up 20X (450ms) -> hold 700ms -> roll up 50X (550ms) -> hold 700ms -> roll up 100X (550ms)
 *   + glow/zoom pulse (600ms) -> hold 4-5s -> loop
 */
const SEQUENCE: MultiplierStep[] = [
  { value: '10X', transitionMs: 450, holdMs: 700 },
  { value: '50X', transitionMs: 550, holdMs: 700 },
  { value: '100X', transitionMs: 550, holdMs: 800 },
];
const SETTLE_VALUE = '100X';
const LANDING_GLOW_MS = 600;
// How far the outgoing value fades (not all the way to 0) as it slides out, so the roll reads as
// one value physically leaving while the next arrives, rather than a hard swap under the mask.
const EXIT_OPACITY = 0.15;
const FINAL_HOLD_MIN_MS = 4000;
const FINAL_HOLD_MAX_MS = 5000;
const FIRST_RUN_DELAY_MS = 900;

@Component({
  selector: 'app-central-banner',
  standalone: false,
  templateUrl: './central-banner.component.html',
  styleUrl: './central-banner.component.scss',
})
export class CentralBannerComponent implements OnDestroy {
  // Only used for the initial SSR/pre-hydration paint - every update after that is driven
  // imperatively on the native elements below so the roll transition always has a real
  // "before" frame committed to animate from (see snapTo/animateTo).
  readonly initialValue = SETTLE_VALUE;

  @ViewChild('reelTrack') private reelTrackRef?: ElementRef<HTMLElement>;
  @ViewChild('reelSlotTop') private slotTopRef?: ElementRef<HTMLElement>;
  @ViewChild('reelSlotBottom') private slotBottomRef?: ElementRef<HTMLElement>;

  isLanding = false;

  private readonly timers: ReturnType<typeof setTimeout>[] = [];

  constructor(@Inject(PLATFORM_ID) private readonly platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      this.schedule(() => this.runSequence(), FIRST_RUN_DELAY_MS);
    }
  }

  ngOnDestroy(): void {
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.length = 0;
  }

  private schedule(fn: () => void, delayMs: number): void {
    this.timers.push(setTimeout(fn, delayMs));
  }

  /**
   * Sets both slot texts/opacities and the track offset with every transition disabled - no
   * visible jump. This is always the "before" frame that animateTo() then transitions away from.
   */
  private snapTo(
    topText: string,
    bottomText: string,
    offsetPercent: number,
    topOpacity: number,
    bottomOpacity: number,
  ): void {
    const track = this.reelTrackRef?.nativeElement;
    const top = this.slotTopRef?.nativeElement;
    const bottom = this.slotBottomRef?.nativeElement;
    if (!track || !top || !bottom) return;
    [track, top, bottom].forEach((el) => (el.style.transitionDuration = '0ms'));
    top.textContent = topText;
    bottom.textContent = bottomText;
    top.style.opacity = `${topOpacity}`;
    bottom.style.opacity = `${bottomOpacity}`;
    track.style.transform = `translateY(-${offsetPercent}%)`;
    void track.offsetWidth; // commit this frame before a transition is armed on top of it
  }

  /** Animates the track offset and both slot opacities together over durationMs - the visible roll. */
  private animateTo(offsetPercent: number, topOpacity: number, bottomOpacity: number, durationMs: number): void {
    const track = this.reelTrackRef?.nativeElement;
    const top = this.slotTopRef?.nativeElement;
    const bottom = this.slotBottomRef?.nativeElement;
    if (!track || !top || !bottom) return;
    [track, top, bottom].forEach((el) => (el.style.transitionDuration = `${durationMs}ms`));
    void track.offsetWidth; // commit the duration change before moving targets, or nothing animates
    track.style.transform = `translateY(-${offsetPercent}%)`;
    top.style.opacity = `${topOpacity}`;
    bottom.style.opacity = `${bottomOpacity}`;
  }

  /** New value enters from below, sliding the current one up and out (resting state: offset 0). */
  private rollUp(nextValue: string, durationMs: number): void {
    const currentVisible = this.slotTopRef?.nativeElement?.textContent ?? SETTLE_VALUE;
    // Prepare: bottom becomes the incoming value, starts faint just under the mask; top (current)
    // stays fully visible and put - this is the frame the roll below animates away from.
    this.snapTo(currentVisible, nextValue, 0, 1, 0);
    // Animate: track rolls up to reveal bottom; top fades out as it exits, bottom fades in as it
    // rises into place, so both values read as physically passing each other mid-roll.
    this.animateTo(50, EXIT_OPACITY, 1, durationMs);
    this.schedule(() => this.snapTo(nextValue, nextValue, 0, 1, 1), durationMs);
  }

  private runSequence(): void {
    let elapsed = 0;
    const last = SEQUENCE[SEQUENCE.length - 1];

    for (const step of SEQUENCE) {
      this.schedule(() => this.rollUp(step.value, step.transitionMs), elapsed);

      if (step === last) {
        // Once the final value has rolled into place, pulse the cyan glow + zoom landing effect.
        this.schedule(() => (this.isLanding = true), elapsed + step.transitionMs);
        this.schedule(() => (this.isLanding = false), elapsed + step.transitionMs + LANDING_GLOW_MS);
      }

      elapsed += step.transitionMs + step.holdMs;
    }

    elapsed += FINAL_HOLD_MIN_MS + Math.random() * (FINAL_HOLD_MAX_MS - FINAL_HOLD_MIN_MS);
    this.schedule(() => this.runSequence(), elapsed);
  }
  popOpen(): void {
    document.body.classList.add("modal-open");
  }
  popClose(): void {
    document.body.classList.remove("modal-open");
  }
}
